import { getSlug, isAuregoShop } from '@allesauto/utils/funcs.ts';
import { ChevronDown20 } from '@carbon/icons-react';
import { Disclosure } from '@headlessui/react';
import clsx from 'clsx';
import React, { FC } from 'react';

import AnimateHeight from '../animateHeight';
import { useWhiteLabel } from '../whiteLabel';

export interface AccordionFilterProps {
  title: string;
  id: string;
  trackingId?: string;
  counter?: number;
  defaultOpen?: boolean;
}

const AccordionFilter: FC<React.PropsWithChildren<AccordionFilterProps>> = (
  props
) => {
  const whiteLabel = useWhiteLabel();
  const { title, children, counter, defaultOpen, trackingId } = props;
  const trackingIdentifier = trackingId ? getSlug(trackingId) : '';
  const trackingIdentifierAccordion = `aa-filterbar--${trackingIdentifier}`;
  const trackingIdentifierAccordionButton = `aa-filterbar--button--${trackingIdentifier}`;
  const trackingIdentifierFilter = `aa-filter--${trackingIdentifier}`;
  const className = {
    accordion: 'border-b-1px last:border-b-0 border-grey20 panel text-white',
    buttonTitle:
      'group-hover:bg-purple90 monochrome:group-hover:bg-brandColor bg-purple80 monochrome:bg-brandColor cursor-pointer flex flex-row items-center pb-30px pl-20px pr-20px pt-30px w-full',
    iconTitle: (open: boolean) =>
      clsx(
        'mr-20px text-green60 monochrome:text-white',
        open
          ? 'rotate-180 transition duration-300'
          : 'rotate-0 transition duration-300'
      ),
    textTitle: (open: boolean) =>
      clsx(
        'leading-[1.875rem] text-white sm:text-[0.875rem] md:text-[1rem] lg:text-[1rem] monochrome:group-hover:text-white group-hover:text-green60',
        open ? 'font-weight-bold' : 'font-weight-normal'
      ),
    optionalCounterTitle: `flex flex-col justify-center ml-auto h-30px w-30px mr-5px bg-white ${
      isAuregoShop(whiteLabel) ? 'rounded-circle-badge' : 'rounded-circle'
    }`,
    optionalCounterTextTitle: `font-regular text-p2 text-purple80 ${
      isAuregoShop(whiteLabel) ? '!text-white' : 'monochrome:text-black'
    } flex justify-center`,
    panel:
      'group-hover:bg-purple90 monochrome:group-hover:bg-brandColor bg-purple80 monochrome:bg-brandColor overflow-x-hidden',
    children: 'pt-10px pb-40px pl-20px pr-20px',
  };

  return (
    <Disclosure
      as="div"
      className={`group ${className.accordion}`}
      data-tracking-id={trackingIdentifierAccordion}
      defaultOpen={defaultOpen}
    >
      {({ open }) => (
        <>
          <Disclosure.Button
            className={className.buttonTitle}
            data-tracking-id={trackingIdentifierAccordionButton}
          >
            <span className={className.iconTitle(open)}>
              <ChevronDown20 />
            </span>
            <span className={className.textTitle(open)}>{title}</span>
            {counter !== undefined && counter > 0 && (
              <span className={className.optionalCounterTitle}>
                <label className={className.optionalCounterTextTitle}>
                  {counter}
                </label>
              </span>
            )}
          </Disclosure.Button>
          <AnimateHeight show={open}>
            <Disclosure.Panel className={className.panel} static>
              <div
                className={className.children}
                data-tracking-id={trackingIdentifierFilter}
              >
                {children}
              </div>
            </Disclosure.Panel>
          </AnimateHeight>
        </>
      )}
    </Disclosure>
  );
};

export default AccordionFilter;
